import { create } from "zustand"
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware"
import { pickRandomSessionAspectRatio } from "./constants"

export type GenerationPromptModifierKey =
  | "race"
  | "age"
  | "bodyType"
  | "facialExpression"
  | "eyeColor"
  | "position"
  | "outfit"
  | "location"
  | "breasts"
  | "hairStyle"
export type GenerationPromptModifiers = Partial<Record<GenerationPromptModifierKey, string>>

interface GenerationState {
  // Settings
  prompt: string
  style: string
  aspectRatio: string
  numImages: number
  negativePrompt: string
  guidanceScale: number
  promptModifiers: GenerationPromptModifiers
  showAdvanced: boolean
  publishToExplore: boolean

  // UI
  historyCollapsed: boolean
  /** null = fresh state (show nothing); string[] = show only these generation IDs */
  activeSessionIds: string[] | null

  // Actions
  setPrompt: (prompt: string) => void
  setStyle: (style: string) => void
  setAspectRatio: (ratio: string) => void
  setNumImages: (count: number) => void
  setNegativePrompt: (prompt: string) => void
  setGuidanceScale: (scale: number) => void
  setPromptModifier: (key: GenerationPromptModifierKey, value: string | null) => void
  clearPromptModifiers: () => void
  setPublishToExplore: (value: boolean) => void
  toggleAdvanced: () => void
  toggleHistory: () => void
  setActiveSession: (ids: string[] | null) => void
  resetSettings: () => void
}

const DEFAULTS = {
  prompt: "",
  style: "realistic",
  aspectRatio: "2:3",
  numImages: 2,
  negativePrompt: "",
  guidanceScale: 3.5,
  promptModifiers: {} as GenerationPromptModifiers,
  showAdvanced: false,
  publishToExplore: true,
  historyCollapsed: true,
  activeSessionIds: null as string[] | null,
}

const memorySessionStorage = new Map<string, string>()

export function getGenerationSessionStorage(): StateStorage {
  if (typeof window === "undefined") {
    return {
      getItem: (key) => memorySessionStorage.get(key) ?? null,
      setItem: (key, value) => {
        memorySessionStorage.set(key, value)
      },
      removeItem: (key) => {
        memorySessionStorage.delete(key)
      },
    }
  }

  try {
    const storage = window.sessionStorage
    const probeKey = "__pleasurai_generation_storage_probe__"
    storage.setItem(probeKey, "1")
    storage.removeItem(probeKey)
    return storage
  } catch {
    return {
      getItem: (key) => memorySessionStorage.get(key) ?? null,
      setItem: (key, value) => {
        memorySessionStorage.set(key, value)
      },
      removeItem: (key) => {
        memorySessionStorage.delete(key)
      },
    }
  }
}

export function hasStoredGenerationSession(storageKey: string): boolean {
  try {
    return getGenerationSessionStorage().getItem(storageKey) !== null
  } catch {
    return false
  }
}

export const useGenerationStore = create<GenerationState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setPrompt: (prompt) => set({ prompt }),
      setStyle: (style) => set({ style }),
      setAspectRatio: (aspectRatio) => set({ aspectRatio }),
      setNumImages: (numImages) => set({ numImages }),
      setNegativePrompt: (negativePrompt) => set({ negativePrompt }),
      setGuidanceScale: (guidanceScale) => set({ guidanceScale }),
      setPromptModifier: (key, value) =>
        set((s) => {
          const next = { ...s.promptModifiers }
          if (value) {
            next[key] = value
          } else {
            delete next[key]
          }
          return { promptModifiers: next }
        }),
      clearPromptModifiers: () => set({ promptModifiers: {} }),
      setPublishToExplore: (publishToExplore) => set({ publishToExplore }),
      toggleAdvanced: () => set((s) => ({ showAdvanced: !s.showAdvanced })),
      toggleHistory: () => set((s) => ({ historyCollapsed: !s.historyCollapsed })),
      setActiveSession: (activeSessionIds) => set({ activeSessionIds }),
      resetSettings: () =>
        set((s) => ({
          ...DEFAULTS,
          aspectRatio: pickRandomSessionAspectRatio(),
          historyCollapsed: s.historyCollapsed, // preserve sidebar state
          publishToExplore: s.publishToExplore, // preserve publish preference
        })),
    }),
    {
      name: "pleasurai-generation",
      version: 3,
      storage: createJSONStorage(getGenerationSessionStorage),
      skipHydration: true,
      partialize: (state) => ({
        prompt: state.prompt,
        style: state.style,
        aspectRatio: state.aspectRatio,
        numImages: state.numImages,
        negativePrompt: state.negativePrompt,
        guidanceScale: state.guidanceScale,
        promptModifiers: state.promptModifiers,
        showAdvanced: state.showAdvanced,
        publishToExplore: state.publishToExplore,
        historyCollapsed: state.historyCollapsed,
        activeSessionIds: state.activeSessionIds,
      }),
      migrate: () => ({ ...DEFAULTS }),
    }
  )
)
